import {
  Add,
  AutoAwesome,
  MoreVert,
  RefreshOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";
import BrandVoiceDetail from "./BrandVoiceDetail";
import BrandVoiceDialog from "./BrandVoiceDialog";
import BrandVoiceGenDialog from "./BrandVoiceGenDialog";

// Brand Voice List Component
export default ({
  currentUser,
  getToken,
  openDialog,
  eventListener,
  companyId,
  company,
  currentPlan,
}: {
  getToken: Function;
  openDialog: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
  company: Company;
  currentPlan: string;
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [brandVoices, setBrandVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<any>(null);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "detail"
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalVoices, setTotalVoices] = useState(0);

  // Fetch voices when component mounts
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchVoices();
      setLoading(false);
    };
    loadData();
  }, [companyId, page, pageSize]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchVoices = async () => {
    setRefreshing(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/brand-voices`, {
        params: { companyId, page: page + 1, pageSize },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        setBrandVoices(response.data.items || []);
        setTotalVoices(response.data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching brand voices:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveVoice = async (voiceData: any) => {
    // If voiceData is null, just refresh the list (used for background generation)
    if (!voiceData) {
      fetchVoices();
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/brand-voices`,
        {
          companyId,
          voice: voiceData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Refresh the voices list
      fetchVoices();
    } catch (error) {
      console.error("Error saving brand voice:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVoice = async (voiceId: string) => {
    openDialog(
      "Delete Brand Voice",
      `Are you sure you want to delete this brand voice?`,
      "Confirm",
      async () => {
        setLoading(true);
        try {
          const token = await getToken();
          await axios.delete(`${hostname}/content/brand-voices/${voiceId}`, {
            params: { companyId },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          // Refresh the voices list
          fetchVoices();

          // If we're in detail view and deleting the current voice, go back to grid
          if (viewMode === "detail" && selectedVoice?.id === voiceId) {
            handleBackToGrid();
          }
        } catch (error) {
          console.error("Error deleting brand voice:", error);
          eventListener({
            type: "SET_ERROR",
            error: parseError(error),
          });
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleViewVoice = (voice: any) => {
    setSelectedVoice(voice);
    setViewMode("detail");
  };

  const handleBackToGrid = () => {
    setSelectedVoice(null);
    setViewMode("grid");
  };

  // Removed redundant handleGenerateVoice function as it's now handled directly in BrandVoiceGenDialog

  // Main Return
  return (
    <Box sx={{ height: "100%", width: "100%", mt: 4 }}>
      {loading && <LinearProgress sx={{ mt: 2 }} />}

      {!loading && viewMode === "grid" && (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditorOpen(true)}
                startIcon={<Add />}
                sx={{ mr: 1 }}
              >
                New Brand Voice
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setGenerateOpen(true)}
                startIcon={<AutoAwesome />}
              >
                Generate with AI
              </Button>
            </Box>
          </Box>

          {brandVoices.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" gutterBottom>
                No Brand Voices Yet
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Create your first Brand Voice Profile to establish consistent
                communication across all channels.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setEditorOpen(true)}
                startIcon={<Add />}
              >
                Create First Brand Voice
              </Button>
            </Paper>
          ) : (
            <Paper
              style={{ marginTop: 20, marginBottom: 40 }}
              variant="outlined"
            >
              {refreshing && <LinearProgress />}
              {!refreshing && <div style={{ height: 4 }}></div>}
              <IconButton
                onClick={() => fetchVoices()}
                style={{ float: "right", marginRight: 5 }}
              >
                <RefreshOutlined />
              </IconButton>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Voice Name</TableCell>
                      <TableCell>Industry</TableCell>
                      <TableCell>Target Audience</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {brandVoices.map((voice: any) => (
                      <TableRow key={voice.id} hover>
                        <TableCell>{voice.name}</TableCell>
                        <TableCell>{voice.industry}</TableCell>
                        <TableCell>{voice.audience}</TableCell>
                        <TableCell>
                          {voice.created_date
                            ? moment(voice.created_date).format("MMMM Do YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {voice.updated_date
                            ? moment(voice.updated_date).format("MMMM Do YYYY")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setAnchorEl(e.currentTarget);
                              setSelectedId(voice.id);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={selectedId === voice.id}
                            onClose={(e: any) => {
                              e.stopPropagation();
                              setAnchorEl(null);
                              setSelectedId(null);
                            }}
                          >
                            <MenuItem
                              onClick={async (e) => {
                                e.stopPropagation();
                                handleViewVoice(voice);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              View Details
                            </MenuItem>
                            <MenuItem
                              onClick={async (e) => {
                                e.stopPropagation();
                                setSelectedVoice(voice);
                                setEditorOpen(true);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteVoice(voice.id);
                                setAnchorEl(null);
                                setSelectedId(null);
                              }}
                            >
                              Delete
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalVoices}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {!loading && viewMode === "detail" && (
        <BrandVoiceDetail
          voice={selectedVoice}
          handleBackToGrid={handleBackToGrid}
          setSelectedVoice={setSelectedVoice}
          handleDeleteVoice={handleDeleteVoice}
          setEditorOpen={setEditorOpen}
          setViewMode={setViewMode}
        />
      )}

      {/* Create/Edit Dialog */}
      <BrandVoiceDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSelectedVoice(null);
        }}
        companyId={companyId}
        company={company}
        onSave={handleSaveVoice}
        existingData={selectedVoice}
        loading={loading}
      />

      {/* Generate with AI Dialog */}
      <BrandVoiceGenDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        companyId={companyId}
        company={company}
        getToken={getToken}
        eventListener={eventListener}
        saveVoice={handleSaveVoice}
      />
    </Box>
  );
};
